import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Btn, Spinner } from "@app/shared/src/components_v2";
import { emptyOrganizationDraft, organizationDraftError, toOrganization } from "../../helpers/organizationDraft";
import { AuthFrame, AuthPanel } from "../layouts/AuthFrame";
import { useOrg } from "../providers/OrgProvider";
import { useSession } from "../providers/SessionProvider";
import { OrganizationFields } from "../shared/OrganizationFields";

/**
 * Where a signed-in user with no organization lands.
 *
 * The console cannot show anything without one — every page is scoped to an organization — so
 * rather than an empty frame with a picker that has nothing in it, the first thing such a user
 * sees is the form that fixes it. The server makes the creator the organization's administrator,
 * so the moment it exists the shell's guard stops sending them here and Overview opens on it.
 *
 * The other way out is named too: someone whose company already uses Omnisight should be invited
 * into the existing organization rather than start a duplicate.
 */
export const OnboardingPage: React.FC = () => {
    const { user, isChecking, logout } = useSession();
    const { orgs, isLoading, error: loadError, createOrg, reload } = useOrg();
    const [draft, setDraft] = useState(emptyOrganizationDraft);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    if (isChecking || (user && isLoading)) {
        return (
            <div className="grid h-full place-items-center bg-[var(--app)] text-[var(--muted)]">
                <Spinner />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (orgs.length > 0) return <Navigate to="/overview" replace />;

    const firstName = (user.name ?? "").trim().split(" ")[0] || user.email;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        const problem = organizationDraftError(draft);
        if (problem) {
            setError(problem);
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await createOrg(toOrganization(draft));
            // The guard above takes over: with an organization in the list this page navigates away.
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not create the organization");
            setBusy(false);
        }
    };

    return (
        <AuthFrame>
            <div className="mb-5">
                <h1 className="text-[22px] font-bold tracking-tight">Welcome, {firstName}</h1>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--muted)]">
                    Your account isn't part of an organization yet. Fleet Commander keeps every truck, event and clip under one, so
                    create yours to get started — you'll be its administrator.
                </p>
            </div>

            {loadError && (
                <div className="mb-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[12.5px] text-[var(--crit)]">
                    {loadError}{" "}
                    <button type="button" onClick={() => void reload()} className="font-semibold underline">
                        Try again
                    </button>
                </div>
            )}

            <AuthPanel onSubmit={submit} className="flex flex-col gap-3">
                <OrganizationFields draft={draft} onChange={setDraft} disabled={busy} autoFocus />
                {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
                <Btn type="submit" size="lg" disabled={busy || !draft.name.trim()} className="mt-1 w-full">
                    {busy ? "Creating…" : "Create organization"}
                </Btn>
            </AuthPanel>

            <p className="mt-4 text-center text-[12px] leading-relaxed text-[var(--faint)]">
                Does your company already use Omnisight? Ask one of its administrators to invite <strong>{user.email}</strong>, then
                sign in again.
            </p>
            <div className="mt-3 text-center text-[12.5px] font-semibold text-[var(--muted)]">
                <button type="button" onClick={() => void logout()} className="hover:text-[var(--text)]">
                    Sign out
                </button>
            </div>
        </AuthFrame>
    );
};
